import type { NodeOperation, ProjectNode } from '@/shared/types/projectData';
import { getNodeId, getProjectIdFromNodeId } from '@/features/projects/utils/projectUtils';

export type LatestNodeOperationMap = Map<string, string>;

function normalizeProjectId(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 && value !== 'default'
    ? value
    : undefined;
}

function nodeOperationKey(nodeId: unknown, fallbackProjectId?: string): string | null {
  if (nodeId === undefined || nodeId === null) {
    return null;
  }

  const rawNodeId = getNodeId(nodeId);
  if (!rawNodeId) {
    return null;
  }

  const projectId = normalizeProjectId(getProjectIdFromNodeId(nodeId, fallbackProjectId));
  return projectId ? `${projectId}:${rawNodeId}` : rawNodeId;
}

function collectOperationNodeKeys(
  operation: NodeOperation,
  fallbackProjectId?: string
): string[] {
  const keys = new Set<string>();
  const add = (nodeId: unknown, projectId = fallbackProjectId) => {
    const key = nodeOperationKey(nodeId, projectId);
    if (key) {
      keys.add(key);
    }
  };

  if (operation.nodeId) {
    add(operation.nodeId);
  }

  if (Array.isArray(operation.nodeIds)) {
    operation.nodeIds.forEach((nodeId) => add(nodeId));
  }

  if (Array.isArray(operation.updates)) {
    operation.updates.forEach((update) => add(update.nodeId));
  }

  if (operation.data?.id) {
    add(operation.data.id, normalizeProjectId(operation.data.projectId) || fallbackProjectId);
  }

  return Array.from(keys);
}

function collectAuthoritativeNodeKeys(
  authoritativeNodes: ProjectNode[],
  fallbackProjectId?: string
): string[] {
  const keys = new Set<string>();

  authoritativeNodes.forEach((node) => {
    const key = nodeOperationKey(node.id, normalizeProjectId(node.projectId) || fallbackProjectId);
    if (key) {
      keys.add(key);
    }
  });

  return Array.from(keys);
}

export function markLatestNodeOperation(
  latestOperations: LatestNodeOperationMap,
  operation: NodeOperation,
  operationId: string,
  fallbackProjectId?: string
): void {
  if (!operationId) {
    return;
  }

  collectOperationNodeKeys(operation, fallbackProjectId).forEach((key) => {
    latestOperations.set(key, operationId);
  });
}

export function isLatestNodeOperationAck(
  latestOperations: LatestNodeOperationMap,
  operation: NodeOperation,
  operationId: string,
  authoritativeNodes: ProjectNode[] = [],
  fallbackProjectId?: string
): boolean {
  if (!operationId) {
    return true;
  }

  const authoritativeKeys = collectAuthoritativeNodeKeys(authoritativeNodes, fallbackProjectId);
  const keys = authoritativeKeys.length > 0
    ? authoritativeKeys
    : collectOperationNodeKeys(operation, fallbackProjectId);

  if (keys.length === 0) {
    return true;
  }

  return keys.every((key) => {
    const latestOperationId = latestOperations.get(key);
    return !latestOperationId || latestOperationId === operationId;
  });
}
